import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import TextareaQueCrece from '../TextareaQueCrece';
import MicButton from '../MicButton';
import { Loader2, Check, PenLine, ChevronDown, ChevronUp, ArrowDownToLine, LineChart, Mic, Square } from 'lucide-react';
import { fechaCorta } from '../../lib/oftalmo';

/**
 * Consulta de hoy (4/11/2026): «La historia, no veo en qué parte se puede
 * escribir».
 *
 * Lo que el médico escribe en la consulta, con los apartados de una consulta
 * de oftalmología, arriba del todo de la historia oftalmológica. Se guarda
 * como el EPISODIO del día de ese médico (el mismo que el evolutivo de
 * siempre: /api/episodios), con cada apartado en su línea con su título; así
 * sale igual en el evolutivo, en los informes y en el Agente, y no hay dos
 * historias. Se guarda solo mientras se escribe.
 */

export const APARTADOS = [
  ['motivo', 'Motivo de consulta y evolución', 'Pérdida de visión del ojo derecho desde hace 2 semanas; qué cuenta, cómo ha ido, qué tratamiento lleva…'],
  ['sa_od', 'Segmento anterior OD', 'Párpados, conjuntiva, córnea, cámara anterior, iris, cristalino…'],
  ['sa_oi', 'Segmento anterior OI', 'Párpados, conjuntiva, córnea, cámara anterior, iris, cristalino…'],
  ['fo_od', 'Fondo de ojo OD', 'Papila, mácula, vasos, periferia…'],
  ['fo_oi', 'Fondo de ojo OI', 'Papila, mácula, vasos, periferia…'],
  ['juicio', 'Juicio clínico', 'Diagnóstico…'],
  ['plan', 'Plan y tratamiento', 'Tratamiento, pruebas, próxima revisión…'],
  ['otras', 'Otras notas', ''],
];
const TITULO = Object.fromEntries(APARTADOS.map(([k, t]) => [k, t.toUpperCase()]));
// Títulos de notas anteriores que se leen dentro de otro apartado: la
// «Anamnesis» y el «Motivo de consulta» de antes van al motivo y evolución.
const TITULOS_ANTIGUOS = { ANAMNESIS: 'motivo', 'MOTIVO DE CONSULTA': 'motivo' };

/** Los apartados → el texto del episodio («TÍTULO: texto», uno por bloque). */
export function componerTexto(campos) {
  return APARTADOS
    .filter(([k]) => (campos[k] || '').trim())
    .map(([k]) => `${TITULO[k]}: ${campos[k].trim()}`)
    .join('\n\n');
}

/** El texto del episodio → los apartados. Lo que no empieza por un título
 *  conocido (un evolutivo escrito a mano antes) va a «Otras notas». */
export function leerTexto(texto) {
  const campos = Object.fromEntries(APARTADOS.map(([k]) => [k, '']));
  if (!texto) return campos;
  const porTitulo = { ...TITULOS_ANTIGUOS, ...Object.fromEntries(APARTADOS.map(([k]) => [TITULO[k], k])) };
  const titulos = Object.keys(porTitulo).map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
  const re = new RegExp(`^(${titulos}):[ \\t]*`, 'gm');
  const marcas = [...texto.matchAll(re)];
  if (!marcas.length) {
    campos.otras = texto.trim();
    return campos;
  }
  const antes = texto.slice(0, marcas[0].index).trim();
  marcas.forEach((m, i) => {
    const fin = i + 1 < marcas.length ? marcas[i + 1].index : texto.length;
    const k = porTitulo[m[1]];
    campos[k] = [campos[k], texto.slice(m.index + m[0].length, fin).trim()].filter(Boolean).join('\n');
  });
  if (antes) campos.otras = [antes, campos.otras].filter(Boolean).join('\n');
  return campos;
}

const hoy = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

function Campo({ k, titulo, ph, valor, onChange, soloLectura }) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{titulo}</span>
        {!soloLectura && <MicButton onText={(t) => onChange(valor ? `${valor.trim()} ${t}` : t)} title={`Dictar: ${titulo}`} />}
      </div>
      <TextareaQueCrece value={valor} onChange={(e) => onChange(e.target.value)} rows={k === 'motivo' || k === 'plan' ? 3 : 2}
        placeholder={ph} readOnly={soloLectura} className="text-sm mt-1" data-testid={`oft-nota-${k}`} />
    </div>
  );
}

/**
 * «Grabar consulta» (4/11/2026): graba la conversación entera con el
 * micrófono del ordenador o del móvil; al pararla, el servidor la transcribe
 * y la reparte en los apartados (services/oftalmo_dictado.py). Aquí se revisa
 * lo entendido y se añade a la consulta. El audio no se guarda.
 */
function GrabarConsulta({ pid, iaActiva, onRepartido }) {
  const [estado, setEstado] = useState('parado'); // parado | grabando | procesando
  const [segundos, setSegundos] = useState(0);
  const grabador = useRef(null);
  const trozos = useRef([]);
  const reloj = useRef(null);
  const flujo = useRef(null);

  const parar = () => { grabador.current?.state === 'recording' && grabador.current.stop(); };
  useEffect(() => () => {
    clearInterval(reloj.current);
    flujo.current?.getTracks().forEach((t) => t.stop());
  }, []);

  const empezar = async () => {
    try {
      flujo.current = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      toast.error('No se puede usar el micrófono: dale permiso en el navegador.');
      return;
    }
    const tipo = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'].find((t) => window.MediaRecorder?.isTypeSupported?.(t)) || '';
    const rec = new MediaRecorder(flujo.current, tipo ? { mimeType: tipo } : undefined);
    trozos.current = [];
    rec.ondataavailable = (e) => { if (e.data?.size) trozos.current.push(e.data); };
    rec.onstop = async () => {
      clearInterval(reloj.current);
      flujo.current?.getTracks().forEach((t) => t.stop());
      const blob = new Blob(trozos.current, { type: rec.mimeType || 'audio/webm' });
      if (blob.size < 2000) { setEstado('parado'); toast.info('La grabación ha quedado vacía.'); return; }
      setEstado('procesando');
      try {
        const fd = new FormData();
        fd.append('file', blob, (rec.mimeType || '').includes('mp4') ? 'consulta.mp4' : 'consulta.webm');
        const r = await api.post(`/api/patients/${pid}/oftalmologia/consulta-audio`, fd, { timeout: 300000 });
        onRepartido(r.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'No se pudo transcribir la consulta');
      } finally {
        setEstado('parado');
      }
    };
    grabador.current = rec;
    rec.start(1000);
    setSegundos(0);
    reloj.current = setInterval(() => setSegundos((x) => x + 1), 1000);
    setEstado('grabando');
  };

  const mmss = `${String(Math.floor(segundos / 60)).padStart(2, '0')}:${String(segundos % 60).padStart(2, '0')}`;
  if (estado === 'grabando') {
    return (
      <Button size="sm" variant="destructive" onClick={parar} data-testid="oft-parar-grabacion">
        <Square className="w-3.5 h-3.5 mr-1.5 fill-current" />Parar y repartir · {mmss}
        <span className="ml-2 w-2 h-2 rounded-full bg-white animate-pulse" />
      </Button>
    );
  }
  if (estado === 'procesando') {
    return <Button size="sm" disabled><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Transcribiendo y repartiendo…</Button>;
  }
  return (
    <Button size="sm" onClick={empezar} disabled={!iaActiva} data-testid="oft-grabar-consulta"
      title={iaActiva ? 'Graba la consulta entera; al parar, se reparte en los apartados' : 'Necesita la IA encendida en la instalación'}>
      <Mic className="w-4 h-4 mr-1.5" />Grabar consulta
    </Button>
  );
}

export default function NotaConsulta({ patient, registros = [], onRegistro, iaActiva = false }) {
  const { user } = useAuth();
  const [episodios, setEpisodios] = useState(null);
  const [campos, setCampos] = useState(() => leerTexto(''));
  const [estado, setEstado] = useState('');
  const [verAnteriores, setVerAnteriores] = useState(true);
  const [propuestas, setPropuestas] = useState([]);
  const [pasando, setPasando] = useState(false);
  const [repartido, setRepartido] = useState(null); // lo que salió de la grabación, para revisar
  const episodio = useRef(null);
  const temporizador = useRef(null);
  const ultimoTexto = useRef('');
  const rol = (user?.role || '').toLowerCase();
  const puedeEscribir = ['admin', 'direccion', 'doctor', 'enfermeria'].includes(rol);

  // Las medidas de lo escrito (PIO, AV, refracción, grosor macular), para
  // pasarlas a las casillas y que salgan en las gráficas (4/11/2026).
  const detectar = useCallback(async (texto) => {
    if (!texto.trim()) { setPropuestas([]); return; }
    try {
      const r = await api.post(`/api/patients/${patient.id}/oftalmologia/extraer`, { texto });
      setPropuestas(r.data.propuestas || []);
    } catch {
      setPropuestas([]);
    }
  }, [patient.id]);

  const cargar = useCallback(async () => {
    try {
      const r = await api.get('/api/episodios', { params: { patient_id: patient.id } });
      const xs = r.data || [];
      setEpisodios(xs);
      const mio = xs.find((e) => e.date === hoy() && e.status === 'abierto' && e.doctor_user_id === user?.id);
      if (mio) {
        episodio.current = mio;
        ultimoTexto.current = mio.texto || '';
        setCampos(leerTexto(mio.texto || ''));
        detectar(mio.texto || '');
      }
    } catch {
      setEpisodios([]);
    }
  }, [patient.id, user?.id, detectar]);
  useEffect(() => { cargar(); }, [cargar]);

  const guardar = useCallback(async (nuevos) => {
    const texto = componerTexto(nuevos);
    if (texto === ultimoTexto.current) return;
    setEstado('guardando');
    try {
      if (!episodio.current) {
        // El episodio de hoy de este médico se crea al escribir la primera
        // letra (no al abrir la ficha: nada de episodios vacíos).
        const r = await api.post('/api/episodios', { patient_id: patient.id, texto: '' });
        episodio.current = r.data;
      }
      const r = await api.put(`/api/episodios/${episodio.current.id}`,
        { texto, visto_updated_at: episodio.current.updated_at });
      episodio.current = r.data;
      ultimoTexto.current = texto;
      setEstado('guardado');
      detectar(texto);
      setEpisodios((xs) => {
        const otros = (xs || []).filter((e) => e.id !== r.data.id);
        return [r.data, ...otros];
      });
    } catch (e) {
      setEstado('error');
      toast.error(e?.response?.data?.detail || 'No se pudo guardar la consulta');
    }
  }, [patient.id, detectar]);

  // Cada medida se apunta con su origen (esta consulta y su tipo): si se
  // corrige el texto, se actualiza el mismo registro en vez de duplicarlo.
  const origenDe = (p) => `consulta:${episodio.current?.id}:${p.tipo}`;
  const yaPasado = (p) => registros.find((x) => x.origen && x.origen === origenDe(p));
  const igual = (p, x) => ['od', 'oi'].every((o) => Object.entries(p[o] || {}).every(([k, v]) => v === null || x[o]?.[k] === v));
  const pasar = async (lista) => {
    setPasando(true);
    try {
      for (const p of lista) {
        const previo = yaPasado(p);
        const { resumen, ...datos } = p;
        const cuerpo = { ...datos, fecha: hoy(), origen: origenDe(p) };
        // eslint-disable-next-line no-await-in-loop
        const r = previo
          ? await api.put(`/api/patients/${patient.id}/oftalmologia/registros/${previo.id}`, cuerpo)
          : await api.post(`/api/patients/${patient.id}/oftalmologia/registros`, cuerpo);
        onRegistro?.(r.data);
      }
      toast.success(lista.length === 1 ? 'Pasado a las casillas' : `${lista.length} medidas pasadas a las casillas`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo pasar a las casillas');
    } finally {
      setPasando(false);
    }
  };
  const pendientes = propuestas.filter((p) => { const x = yaPasado(p); return !x || !igual(p, x); });

  const camposRef = useRef(campos);
  camposRef.current = campos;
  const cambiar = (k, v) => {
    if (!puedeEscribir) return;
    const nuevos = { ...camposRef.current, [k]: v };
    camposRef.current = nuevos;
    setCampos(nuevos);
    setEstado('escribiendo');
    clearTimeout(temporizador.current);
    temporizador.current = setTimeout(() => guardar(nuevos), 1200);
  };
  useEffect(() => () => clearTimeout(temporizador.current), []);

  const anadirRepartido = () => {
    const nuevos = { ...camposRef.current };
    Object.entries(repartido.apartados || {}).forEach(([k, v]) => {
      if (!(v || '').trim() || !(k in nuevos)) return;
      nuevos[k] = nuevos[k]?.trim() ? `${nuevos[k].trim()}\n${v.trim()}` : v.trim();
    });
    camposRef.current = nuevos;
    setCampos(nuevos);
    setRepartido(null);
    clearTimeout(temporizador.current);
    guardar(nuevos);
    toast.success('Consulta añadida a la historia');
  };

  const anteriores = (episodios || []).filter((e) => e.id !== episodio.current?.id && (e.texto || '').trim());

  return (
    <div className="cp-card p-4 sm:p-5" data-testid="oft-nota-consulta">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="font-display font-semibold text-[#13231E] dark:text-foreground flex items-center gap-2">
          <PenLine className="w-4 h-4" /> Consulta de hoy · {fechaCorta(hoy())}
          <span className="text-xs font-normal text-muted-foreground">{user?.name}</span>
        </h3>
        <div className="flex flex-wrap items-center gap-3">
        {puedeEscribir && <GrabarConsulta pid={patient.id} iaActiva={iaActiva} onRepartido={setRepartido} />}
        <span className="text-xs text-muted-foreground flex items-center gap-1" data-testid="oft-nota-estado">
          {estado === 'guardando' && <><Loader2 className="w-3.5 h-3.5 animate-spin" />Guardando…</>}
          {estado === 'guardado' && <><Check className="w-3.5 h-3.5 text-emerald-600" />Guardado</>}
          {estado === 'escribiendo' && 'Escribiendo…'}
          {estado === 'error' && <span className="text-red-600">Sin guardar</span>}
          {!estado && (puedeEscribir ? 'Se guarda sola mientras escribes' : 'Solo lectura')}
        </span>
        </div>
      </div>
      {episodios === null ? (
        <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#2E6E5E]" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-3">
          <div className="md:col-span-2"><Campo k="motivo" titulo={APARTADOS[0][1]} ph={APARTADOS[0][2]} valor={campos.motivo} onChange={(v) => cambiar('motivo', v)} soloLectura={!puedeEscribir} /></div>
          {APARTADOS.slice(1, 5).map(([k, t, ph]) => (
            <Campo key={k} k={k} titulo={t} ph={ph} valor={campos[k]} onChange={(v) => cambiar(k, v)} soloLectura={!puedeEscribir} />
          ))}
          {APARTADOS.slice(5).map(([k, t, ph]) => (
            <div key={k} className="md:col-span-2"><Campo k={k} titulo={t} ph={ph} valor={campos[k]} onChange={(v) => cambiar(k, v)} soloLectura={!puedeEscribir} /></div>
          ))}
        </div>
      )}
      {propuestas.length > 0 && (
        <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50/60 dark:bg-emerald-950/20 p-3" data-testid="oft-detectado">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
            <div className="text-sm font-semibold flex items-center gap-1.5"><LineChart className="w-4 h-4" />Detectado en lo escrito</div>
            {puedeEscribir && pendientes.length > 1 && (
              <Button size="sm" onClick={() => pasar(pendientes)} disabled={pasando} data-testid="oft-pasar-todo">
                {pasando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ArrowDownToLine className="w-4 h-4 mr-1" />}Pasar todo a las casillas
              </Button>
            )}
          </div>
          <div className="space-y-1">
            {propuestas.map((p) => {
              const x = yaPasado(p);
              const alDia = x && igual(p, x);
              return (
                <div key={p.tipo} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                  <span>{p.resumen}</span>
                  {alDia ? <span className="text-xs text-emerald-700 flex items-center gap-1"><Check className="w-3.5 h-3.5" />En las casillas y las gráficas</span>
                    : puedeEscribir && (
                      <Button size="sm" variant="outline" onClick={() => pasar([p])} disabled={pasando} data-testid={`oft-pasar-${p.tipo}`}>
                        <ArrowDownToLine className="w-3.5 h-3.5 mr-1" />{x ? 'Actualizar casillas' : 'Pasar a casillas'}
                      </Button>
                    )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div className="text-[11px] text-muted-foreground mt-2">
        Si escribes las medidas (p. ej. «PIO 18/20», «AVcc OD 0,8 OI 1», «OD −2,00 −0,50 × 90», «grosor central 320», «Kmax 52,3», «paquimetría 540»), salen arriba para pasarlas a las casillas y a las gráficas. Esta consulta queda también en «Evolutivo y antecedentes».
      </div>

      {anteriores.length > 0 && (
        <div className="mt-4 border-t pt-3">
          <button type="button" className="text-sm font-semibold flex items-center gap-1" onClick={() => setVerAnteriores(!verAnteriores)}>
            {verAnteriores ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            Consultas anteriores ({anteriores.length})
          </button>
          {verAnteriores && (
            <div className="mt-2 space-y-3 max-h-[420px] overflow-y-auto pr-1" data-testid="oft-consultas-anteriores">
              {anteriores.map((e) => {
                const c = leerTexto(e.texto);
                return (
                  <div key={e.id} className="rounded-lg border p-3 text-sm">
                    <div className="text-xs text-muted-foreground mb-1"><b className="text-foreground">{fechaCorta(e.date)}</b> · {e.doctor_name}{e.status === 'abierto' ? ' · abierta' : ''}</div>
                    {APARTADOS.filter(([k]) => c[k]).map(([k, t]) => (
                      <div key={k} className="mb-1"><span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{t}: </span><span className="whitespace-pre-wrap">{c[k]}</span></div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
      {repartido && (
        <Dialog open={!!repartido} onOpenChange={(o) => !o && setRepartido(null)}>
          <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="oft-repartido">
            <DialogHeader><DialogTitle>Consulta grabada · revisa antes de añadir</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {APARTADOS.filter(([k]) => (repartido.apartados?.[k] || '').trim()).length === 0 && (
                <div className="text-sm text-muted-foreground">No se ha entendido nada clínico en la grabación.</div>
              )}
              {APARTADOS.filter(([k]) => (repartido.apartados?.[k] || '').trim()).map(([k, t]) => (
                <div key={k}>
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{t}</div>
                  <TextareaQueCrece value={repartido.apartados[k]} rows={2} className="text-sm mt-1"
                    onChange={(e) => setRepartido((x) => ({ ...x, apartados: { ...x.apartados, [k]: e.target.value } }))} />
                </div>
              ))}
              <details className="text-xs text-muted-foreground">
                <summary className="cursor-pointer">Ver la transcripción completa</summary>
                <div className="whitespace-pre-wrap mt-1">{repartido.transcripcion}</div>
              </details>
              <div className="text-[11px] text-muted-foreground">Lo ha repartido la IA: revísalo. No se añade nada hasta que pulses «Añadir a la consulta». El audio no se guarda.</div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRepartido(null)}>Descartar</Button>
              <Button onClick={anadirRepartido} data-testid="oft-anadir-repartido">Añadir a la consulta</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
